package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.{TestTranscriptMetaData, TestTranscriptSearchData}
import com.neurobs.errors.Result
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.AdminPanelController
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.dom.{AnchorLink, ElementFactory, RecordTableView, SelectSelectBox}
import com.neurobs.js.util.StringUtilities.dateTimeString
import com.neurobs.js.view.{Gui, ValueSelectBox}
import org.scalajs.dom.raw.HTMLElement
import scalatags.JsDom.all._

import scala.concurrent.{Future, Promise}

object TestTranscriptListController {
	import com.neurobs.ccabserver.administrator.controller.TableNavigationController._

	case class CustomData
	(
		completed: Option[Boolean] = None,
		hasError: Option[Boolean] = None,
		processed: Option[Boolean] = None,
	)

	class TestTranscriptSearchForm extends SearchForm[CustomData] {
		class YesNoBox {
			val select = makeSelectBox(width = 10)
			val value = ValueSelectBox.yesNo(SelectSelectBox(select))
		}

		private val completedBox = new YesNoBox
		private val hasErrorBox = new YesNoBox
		private val processedBox = new YesNoBox

		override def container: HTMLElement = {
			val temp = ElementFactory.makeFormTable(Array(
				("Completed:", completedBox.select),
				("Has Error:", hasErrorBox.select),
				("Processed:", processedBox.select)
			))
			temp.style.margin = "0"
			temp
		}

		override def set(s: CustomData): Unit = {
			completedBox.value.setSelectedValue(s.completed)
			hasErrorBox.value.setSelectedValue(s.hasError)
			processedBox.value.setSelectedValue(s.processed)
		}

		override def get: Result[CustomData] = Right(CustomData(
			completed = completedBox.value.value,
			hasError = hasErrorBox.value.value,
			processed = processedBox.value.value
		))

		override def clear(): Unit = {
			completedBox.value.setSelectedValue(None)
			hasErrorBox.value.setSelectedValue(None)
			processedBox.value.setSelectedValue(None)
		}
	}

	class TestTranscriptAdapter(gui: Gui) extends Adapter[CustomData, TestTranscriptMetaData] {
		override def getId(d: TestTranscriptMetaData): Long = d.id

		override def get(search: NavigationSearchData[CustomData]): Future[Result[Seq[TestTranscriptMetaData]]] = {
			val temp = TestTranscriptSearchData(
				completed = search.data.completed,
				hasError = search.data.hasError,
				processed = search.data.processed,
				minTime = search.minTime,
				maxTime = search.maxTime,
				maxId = search.maxId,
				limit = search.limit
			)
			val p = Promise[Result[Seq[TestTranscriptMetaData]]]
			CCABServer.searchTestTranscripts(temp) {
				case Left(error) => p.success(Left(error.message))
				case Right(data) => p.success(Right(data))
			}
			p.future
		}

		override def columnHeaders: Seq[RecordTableView.ColumnHeader] = { Seq(
			"DID",
			"Time",
			"Config",
			"Software",
			"Run",
			"Completed",
			"Error",
			"Trans",
			"Processed",
			"Sync"
		)}

		override def recordRow(a: TestTranscriptMetaData): Seq[RecordTableView.CellContent] = {
			val errorElement = a.error match {
				case Some(e) =>
					val contentLink = AnchorLink(makeLink(text = "view"))
					contentLink.setHandler { () =>
						gui.showModalFormattedText(width = 50, title = s"Transcript ${a.id} Error", e)
					}
					contentLink.anchor
				case None => span().render
			}

			val transcriptElement = if (a.processed.isDefined && a.error.isEmpty) {
				val link = AnchorLink(makeLink(text = "view"))
				link.setHandler { () =>
					CCABServer.getTestTranscript(a.id) {
						case Right(data) => gui.showModalFormattedText(width = 50, title = s"Transcript ${a.id}", data.transcript.getOrElse(""))
						case Left(_) =>
					}
				}
				link.anchor
			}
			else span().render

			Seq(
				a.id.toString,
				dateTimeString(a.createdAt),
				a.configVersion.toString,
				a.softwareVersion.toString,
				a.testRunId.toString,
				a.completedAt.map(dateTimeString),
				errorElement,
				transcriptElement,
				a.processed.map(dateTimeString),
				a.needsSync.toString
			)
		}

		override def createSearchForm: SearchForm[CustomData] = new TestTranscriptSearchForm
	}

	def apply(gui: Gui): AdminPanelController = {
		val adapter = new TestTranscriptAdapter(gui)
		new TableNavigationPanelController(title = "Test Transcripts", adapter)
	}
}
